import { useEffect, useState } from 'preact/hooks';
import { useStore } from '../store';
import { cap, coatLabel, fmtMoney, speciesLabel } from '../bridge';
import { IconCamera, IconWarn, IconBinocs } from '../components/icons';
import { Sheet } from '../components/Sheet';
import { ObserveSheet } from '../components/ObserveSheet';
import { buildAlertFeed, isProblemAlert, alertSeverity, observeAlertIdentity, type AlertItem } from '../alertTone';

export function ReserveScreen() {
  const { sim, bridge, openPanel, mode, setMode } = useStore();
  const [holdingsOpen, setHoldingsOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [speciesFilter, setSpeciesFilter] = useState<string | null>(null);
  // The sim emits no alert when an observation finishes, so the finished report itself (recentReports) is the signal.
  // Reports already present at load are treated as seen; only a report that lands while you play shows the pill.
  const latestReport = sim.observe?.recentReports.at(-1) ?? null;
  const [obsFocus, setObsFocus] = useState<{ species: string; tick: number } | null>(null);
  const [seenTick, setSeenTick] = useState<number | null | undefined>(undefined);
  useEffect(() => {
    if (seenTick === undefined && sim.observe !== null) setSeenTick(latestReport?.completedTick ?? -1);
  }, [seenTick, sim.observe, latestReport]);
  // Sim 'observe_complete' alert (refId observe:<species>:<completedTick>) is the same completion as its report: when it exists, the alert pill is the single notification.
  const feed = buildAlertFeed(sim.alerts, sim.alertEvents);
  const mutedTypes = sim.alertPolicy?.mutedTypes ?? [];
  const obsAlertTick = (a: AlertItem): number | null => {
    const id = observeAlertIdentity(a);
    return id !== null && (sim.observe?.recentReports.some((r) => r.completedTick === id.tick) ?? false) ? id.tick : null;
  };
  // Readable label from the alert's identity (refId carries the species id), never from its wording.
  const obsSpecies = (a: AlertItem): string | null => {
    const id = observeAlertIdentity(a);
    return id === null ? null : cap(id.species.replace(/_/g, ' '));
  };
  const alertLabel = (a: AlertItem): string => {
    if (a.type === 'animal_recovered' && a.refId !== null) {
      const animal = sim.animals.find(animal => animal.id === a.refId);
      if (animal) return `${animal.name} (${speciesLabel(animal.species)}) recovered under veterinary care`;
    }
    const sp = obsSpecies(a);
    return sp === null ? a.text : `Observed ${sp}${obsAlertTick(a) !== null ? ' - read notes' : ''}`;
  };
  const openReport = (tick: number): void => {
    const r = sim.observe?.recentReports.find((x) => x.completedTick === tick);
    if (r) setObsFocus({ species: r.species, tick });
  };
  const freshReport = latestReport !== null && seenTick !== undefined && seenTick !== null && latestReport.completedTick > seenTick && !sim.alertEvents.some((e) => e.type === 'observe_complete' && e.refId?.endsWith(`:${String(latestReport.completedTick)}`)) ? latestReport : null;
  useEffect(() => {
    const open = () => { setHoldingsOpen(true); };
    window.addEventListener('ea:holdings', open);
    return () => { window.removeEventListener('ea:holdings', open); };
  }, []);
  if (mode === 'field') return null; // FieldHud owns the viewport in field mode
  return (
    <>
      {/* QA baseline: clean full-bleed scene - alerts as compact pills, holdings collapsed
          to a small pill, field mode via a floating amber button (refs/mockup-reserve-*.png). */}
      <div class="screen-scroll" style="justify-content:flex-end;pointer-events:none">
        {(() => {
          // Dedupe + cap so alert floods never cover the world view.
          const alerts = feed.filter(a => a.type === undefined || !mutedTypes.includes(a.type));
          const problem = (a: AlertItem): boolean => isProblemAlert(a.type);
          // Newest two stay on the scene (a stale old alert must not hold a slot); the rest open in a full list.
          const visible = alerts.slice(-2);
          const extra = alerts.length - visible.length;
          return (
            <div style="display:flex;flex-direction:column;align-items:flex-start;gap:8px;margin-bottom:68px;pointer-events:auto">
              {freshReport && (
                <button class="pill" aria-label={`Observation finished: ${freshReport.species.replace(/_/g, ' ')}, plus ${String(freshReport.researchPoints)} research. Tap to read the notes`} onClick={() => { setSeenTick(freshReport.completedTick); setObsFocus({ species: freshReport.species, tick: freshReport.completedTick }); }} style="font-size:11px;padding:3px 10px;align-self:flex-start;margin-left:12px;max-width:calc(100% - 130px);white-space:normal;text-align:left;cursor:pointer;min-height:32px">
                  <IconBinocs style="width:12px;height:12px" />Observed {freshReport.species.replace(/_/g, ' ')} - read notes
                </button>
              )}
              {visible.map((a) => (
                <button key={a.refId ?? a.text} class={problem(a) ? 'pill pill-red' : 'pill'} aria-label={`${problem(a) ? alertSeverity(a.type) : 'Update'}: ${alertLabel(a)}. Tap to read all alerts`} onClick={() => { const t = obsAlertTick(a); if (t !== null) openReport(t); else setAlertsOpen(true); }} style="font-size:11px;padding:3px 10px;align-self:flex-start;margin-left:12px;max-width:calc(100% - 130px);white-space:normal;text-align:left;cursor:pointer;min-height:32px">
                  {problem(a) && <IconWarn style="width:12px;height:12px" />}{alertLabel(a)}
                </button>
              ))}
              {(extra > 0 || feed.length !== alerts.length) && (
                <button class="pill" onClick={() => { setAlertsOpen(true); }} style="font-size:11px;padding:4px 12px;opacity:0.95;cursor:pointer;min-height:32px;align-self:flex-start;margin-left:12px">Alerts inbox ({sim.alertPolicy?.unreadCount ?? feed.length} unread)</button>
              )}
            </div>
          );
        })()}
      </div>

      {obsFocus && <ObserveSheet initialSpecies={obsFocus.species} focusTick={obsFocus.tick} onClose={() => { setObsFocus(null); }} />}
      {alertsOpen && (
        <Sheet label="Today's alerts" onClose={() => { setAlertsOpen(false); }}>
          <div style="padding:6px 18px 16px">
            <div class="eyebrow eyebrow-rule">Today's alerts</div>
            <div class="row-label" style="white-space:normal;text-transform:none;letter-spacing:0.01em;margin-top:6px">Newest first. Muting a type removes its scene notifications and automatic pauses, but keeps its history here. Repeated alerts are grouped.</div>
            <button class="btn btn-quiet" style="margin-top:8px;width:100%" onClick={()=>{bridge.send('alerts.markRead',{});}}>Mark current alerts read ({sim.alertPolicy?.unreadCount ?? feed.length} unread)</button>
            {mutedTypes.length>0&&<div class="card" style="padding:10px;margin-top:8px"><div class="eyebrow">Muted event types</div>{mutedTypes.map(type=><div class="row-label" style="white-space:normal" key={type}>{type.replace(/_/g,' ')} <button class="btn btn-quiet" onClick={()=>{bridge.send('alerts.mute.set',{type,muted:false});}}>Unmute</button></div>)}</div>}
            {feed.slice().reverse().map((a, i) => {
              const tick = obsAlertTick(a);
              const style = 'border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:8px;padding:8px 12px;font-size:13px;white-space:normal;word-break:break-word;text-align:left;width:100%';
              const key = `${String(i)}:${a.refId ?? a.text}`;
              if (tick !== null) {
                return <button key={key} class="card" aria-label={`${alertLabel(a)}. Opens the report`} onClick={() => { setAlertsOpen(false); openReport(tick); }} style={`${style};cursor:pointer;min-height:44px`}>{a.text.replace(/_/g, ' ')}<div class="row-label">Tap to read the notes</div></button>;
              }
              return (
                <div key={key} class={isProblemAlert(a.type) ? 'card pill-red' : 'card'} style={style}>
                  {alertSeverity(a.type).replace(/^./, c => c.toUpperCase())}: {alertLabel(a)}
                  {a.type !== undefined && <button class="btn btn-quiet" style="display:block;margin-top:6px" onClick={()=>{if(a.type)bridge.send('alerts.mute.set',{type:a.type,muted:!mutedTypes.includes(a.type)});}}>{mutedTypes.includes(a.type)?'Unmute this event type':'Mute this event type'}</button>}
                  {obsSpecies(a) !== null && <div class="row-label">Notes are no longer in the recent reports</div>}
                </div>
              );
            })}
            {sim.alerts.length === 0 && <p style="font-size:13px;color:var(--ui-ink-soft)">No alerts today.</p>}
          </div>
        </Sheet>
      )}

      {/* Expanded holdings card */}
      {holdingsOpen && (
        <div class="holdings-sheet card" style="padding:14px 16px">
          <div style="display:flex;align-items:center;justify-content:space-between">
            <span class="eyebrow eyebrow-rule" style="flex:1">Holdings</span>
            <button class="holdings-close" aria-label="Collapse holdings" onClick={() => { setHoldingsOpen(false); }}>-</button>
          </div>
          <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-top:10px;text-align:center">
            <div><div class="row-value">${fmtMoney(sim.cashCents)}</div><div class="row-label">Cash</div></div>
            <div><div class="row-value">{sim.animals.length}</div><div class="row-label">Animals</div></div>
            <div><div class="row-value">{sim.parcels.filter((x) => x.owned).length}</div><div class="row-label">Parcels</div></div>
            <div><div class="row-value">{sim.enclosures.length}</div><div class="row-label">Enclosures</div></div>
            <div><div class="row-value">{sim.staff.length}</div><div class="row-label">Staff</div></div>
            <div><div class="row-value">{new Set(sim.animals.map((a) => a.species)).size}</div><div class="row-label">Species</div></div>
          </div>
          {(() => {
            // Herd by species and coat (morph), with species filter chips once the herd mixes.
            const bySpecies = new Map<string, Map<string, number>>();
            for (const a of sim.animals) {
              const coats = bySpecies.get(a.species) ?? new Map<string, number>();
              coats.set(a.coat, (coats.get(a.coat) ?? 0) + 1);
              bySpecies.set(a.species, coats);
            }
            const species = [...bySpecies.keys()].sort((a, b) => (bySpecies.get(b)?.size ?? 0) - (bySpecies.get(a)?.size ?? 0));
            const shown = speciesFilter && bySpecies.has(speciesFilter) ? [speciesFilter] : species;
            return (
              <>
                <div class="eyebrow" style="margin-top:12px">Herd by species</div>
                {species.length > 2 && (
                  <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:6px">
                    <button class={`pill${speciesFilter === null ? ' pill-amber' : ''}`} style="cursor:pointer" onClick={() => { setSpeciesFilter(null); }}>All</button>
                    {species.map((sp) => (
                      <button key={sp} class={`pill${speciesFilter === sp ? ' pill-amber' : ''}`} style="cursor:pointer" onClick={() => { setSpeciesFilter(speciesFilter === sp ? null : sp); }}>{sp}</button>
                    ))}
                  </div>
                )}
                {shown.map((sp) => {
                  const coats = bySpecies.get(sp) ?? new Map<string, number>();
                  const total = [...coats.values()].reduce((a, n) => a + n, 0);
                  const rareCount = sim.animals.filter((a) => a.species === sp && a.rare).length;
                  return (
                    <div key={sp} class="row" style="margin:0;border:none;padding:5px 0">
                      <span class="row-main">
                        <div class="row-value" style="font-size:13px">{cap(sp)}{rareCount > 0 ? ` - ${rareCount} rare` : ''}</div>
                        <div class="row-label" style="text-transform:none;letter-spacing:0.01em">{[...coats.entries()].map(([c, n]) => `${coatLabel(sp, c)} x${n}`).join(', ')}</div>
                      </span>
                      <span class="row-value" style="font-size:14px">{total}</span>
                    </div>
                  );
                })}
                {sim.animals.length === 0 && <div class="row-label" style="margin-top:6px">No animals yet.</div>}
              </>
            );
          })()}
          <div style="display:flex;gap:10px;margin-top:12px">
            <button class="btn btn-quiet" style="flex:1" onClick={() => { bridge.monetization.onEvent('reserve.summary.viewed', {}); }}>Summary</button>
            <button class="btn btn-quiet" style="flex:1" onClick={() => { openPanel({ kind: 'photo' }); }}><IconCamera />Photo</button>
          </div>
          <button class="btn btn-primary" style="width:100%;margin-top:10px" onClick={() => { setHoldingsOpen(false); setMode('field'); }}>
            <IconBinocs style="width:18px;height:18px" />Enter field mode
          </button>
        </div>
      )}

      {!holdingsOpen && (
        <button class="field-entry" aria-label="Enter field mode" onClick={() => { setMode('field'); }}>
          <IconBinocs />Field
        </button>
      )}
    </>
  );
}
